import React, { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const btn = "rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] transition-colors";

const RecoveryCodes = ({ codes }) => (
  <div className="mt-5 rounded-xl border border-amber-400/30 bg-amber-400/5 p-4" data-testid="totp-recovery-codes">
    <p className="text-xs text-amber-200">Save these recovery codes somewhere safe. Each works once if you lose your phone. They won't be shown again.</p>
    <pre className="mt-3 grid grid-cols-2 gap-1 font-mono text-sm text-zinc-100 select-all">{codes.join("\n")}</pre>
  </div>
);

const TotpSetup = ({ status, reload, cfg }) => {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [err, setErr] = useState("");
  const call = (fn) => fn().catch((e) => setErr(e.response?.data?.detail || "Something went wrong."));
  const start = () => call(async () => { setErr(""); setSetup((await axios.post(`${API}/admin/totp/enroll`, {}, cfg)).data); });
  const confirm = () => call(async () => { setCodes((await axios.post(`${API}/admin/totp/confirm`, { code }, cfg)).data.recovery_codes); setSetup(null); reload(); });
  const disable = () => call(async () => { await axios.post(`${API}/admin/totp/disable`, { code }, cfg); setCode(""); reload(); });
  const codeInput = (
    <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit code"
      data-testid="totp-code-input" className="w-40 rounded-lg bg-black/40 border border-white/10 px-4 py-2.5 text-sm tracking-[0.3em] focus:outline-none focus:border-purple-500/50" />
  );
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="totp-card">
      <div className="flex items-center gap-2 text-zinc-100"><ShieldCheck size={16} className="text-emerald-400" /> 2-step login (authenticator app)</div>
      <p className="mt-2 text-sm text-zinc-400" data-testid="totp-status">
        {status.enabled ? `On — ${status.recovery_left} recovery codes left.` : "Off — anyone with your admin key and device can sign in."}
      </p>
      {!status.enabled && !setup && <button onClick={start} data-testid="totp-start-btn" className={`${btn} mt-4 bg-purple-700 hover:bg-purple-600`}>Turn on</button>}
      {setup && (
        <div className="mt-4 flex flex-col sm:flex-row gap-6">
          <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="Scan with your authenticator app" className="w-44 h-44 rounded-lg bg-white p-2" data-testid="totp-qr" />
          <div className="space-y-3">
            <p className="text-sm text-zinc-300">Scan with Google Authenticator (or similar), then enter the 6-digit code.</p>
            <p className="text-[11px] text-zinc-500 break-all">Can't scan? Key: <span className="font-mono text-zinc-300" data-testid="totp-manual-key">{setup.manual_key}</span></p>
            <div className="flex gap-2">{codeInput}<button onClick={confirm} data-testid="totp-confirm-btn" className={`${btn} bg-emerald-600 hover:bg-emerald-500`}>Confirm</button></div>
          </div>
        </div>
      )}
      {status.enabled && !codes && (
        <div className="mt-4 flex gap-2">{codeInput}<button onClick={disable} data-testid="totp-disable-btn" className={`${btn} border border-red-500/40 text-red-300 hover:bg-red-500/10`}>Turn off</button></div>
      )}
      {codes && <RecoveryCodes codes={codes} />}
      {err && <p className="mt-3 text-xs text-red-400" data-testid="totp-error">{err}</p>}
    </div>
  );
};

const BackupCard = ({ cfg }) => {
  const [state, setState] = useState("");
  const send = async () => {
    setState("sending");
    try { const { data } = await axios.post(`${API}/admin/backup/send`, {}, cfg); setState(`Sent to ${data.to}`); }
    catch (e) { setState(e.response?.data?.detail || "Backup failed."); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="backup-card">
      <div className="text-zinc-100">Weekly data backup</div>
      <p className="mt-2 text-sm text-zinc-400">Every Monday a backup of inquiries, leads, bookings, CRM, chats and payments is emailed to you.</p>
      <button onClick={send} disabled={state === "sending"} data-testid="backup-send-btn" className={`${btn} mt-4 border border-white/15 text-zinc-200 hover:border-purple-400/50`}>
        {state === "sending" ? <Loader2 size={13} className="animate-spin inline" /> : "Send backup now"}
      </button>
      {state && state !== "sending" && <p className="mt-3 text-xs text-zinc-400" data-testid="backup-status">{state}</p>}
    </div>
  );
};

export default function SecurityPanel({ adminKey }) {
  const [status, setStatus] = useState(null);
  const cfg = {};
  const reload = () => axios.get(`${API}/admin/totp/status`, cfg).then(({ data }) => setStatus(data)).catch(() => setStatus({ error: true }));
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!status) return <Loader2 className="animate-spin text-zinc-400" size={16} />;
  if (status.error) return <p className="text-sm text-red-400" data-testid="security-panel-error">Couldn't load security settings. Lock and unlock admin, then try again.</p>;
  return (
    <div className="grid gap-6 md:grid-cols-2" data-testid="security-panel">
      <TotpSetup status={status} reload={reload} cfg={cfg} />
      <BackupCard cfg={cfg} />
    </div>
  );
}
